import React from 'react';
import { LineChart, BarChart3, TrendingUp, DollarSign, Users, Target, Activity, Zap, Calculator } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { openBookDemo } from '../utils/config';

const ROIReporting = ({ onROICalculatorClick }) => {
  const { t } = useTranslation();

  const reportingFeatures = [
    {
      icon: LineChart,
      title: t('roi.features.realTime.title'),
      description: t('roi.features.realTime.description'),
      color: "blue"
    },
    {
      icon: BarChart3,
      title: t('roi.features.executive.title'),
      description: t('roi.features.executive.description'),
      color: "navy"
    },
    {
      icon: TrendingUp,
      title: t('roi.features.automated.title'),
      description: t('roi.features.automated.description'),
      color: "indigo"
    },
    {
      icon: DollarSign,
      title: t('roi.features.correlation.title'),
      description: t('roi.features.correlation.description'),
      color: "cyan"
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <p className="text-sm text-blue-600 uppercase tracking-wider mb-4 font-semibold">CoachMeUpp Insights™</p>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t('roi.title')}
          </h2>
          <p className="text-lg text-gray-600">
            {t('roi.subtitle')}
          </p>
        </div>

        {/* Main ROI Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-16 max-w-5xl mx-auto">
          <div className="p-6 rounded-lg bg-white border-2 border-gray-200 hover:border-red-400 text-center transition-all duration-300 hover:shadow-lg">
            <div className="text-4xl font-bold text-gray-900 mb-2">3.5x</div>
            <div className="text-gray-900 font-semibold mb-1">ROI</div>
            <div className="text-sm text-gray-600">{t('benefits.metrics.roi.description')}</div>
          </div>
          <div className="p-6 rounded-lg bg-white border-2 border-gray-200 hover:border-blue-400 text-center transition-all duration-300 hover:shadow-lg">
            <div className="text-4xl font-bold text-gray-900 mb-2">10+</div>
            <div className="text-gray-900 font-semibold mb-1">{t('roi.features.automated.title')}</div>
            <div className="text-sm text-gray-600">{t('roi.features.automated.description')}</div>
          </div>
          <div className="p-6 rounded-lg bg-white border-2 border-gray-200 hover:border-gray-400 text-center transition-all duration-300 hover:shadow-lg">
            <div className="text-4xl font-bold text-gray-900 mb-2">24/7</div>
            <div className="text-gray-900 font-semibold mb-1">{t('roi.features.realTime.title')}</div>
            <div className="text-sm text-gray-600">{t('roi.features.realTime.description')}</div>
          </div>
        </div>

        {/* Reporting Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-7xl mx-auto">
          {reportingFeatures.map((feature, index) => {
            const Icon = feature.icon;
            
            return (
              <div 
                key={feature.title || `roi-${index}`}
                className="p-6 rounded-lg bg-white border border-gray-200 hover:border-red-300 hover:shadow-md transition-all duration-300"
              >
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 text-gray-700 mb-3">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-16 p-8 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl max-w-4xl mx-auto border border-blue-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            {t('roiCalculator.ctaTitle', 'Calculate Your Coaching ROI')}
          </h3>
          <p className="text-gray-600 mb-6">
            {t('roiCalculator.ctaDescription', 'Use our interactive calculator to estimate the potential return on investment from implementing a digital coaching program in your organization.')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={onROICalculatorClick}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
              data-testid="roi-section-calculator-btn"
            >
              <Calculator className="w-5 h-5" />
              {t('roiCalculator.calculateBtn', 'Calculate ROI')}
            </button>
            <button 
              onClick={openBookDemo}
              className="inline-flex items-center justify-center px-8 py-4 bg-white hover:bg-gray-50 text-gray-900 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border border-gray-300"
            >
              {t('nav.bookDemo')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROIReporting;
